<template>
	<AppHeader
		:logo="logo"
		:headerButton1="headerButton1"
		:headerButton2="headerButton2"
	/>

	<div class="flex-grow-1">
		<UiElement class="d-flex flex-column h-100 py-0" :settings="appearance.body.settings">
			<template v-if="page.options.view.value === 'grid'">
				<div style="width: 1080px;" :style="{ 'margin-left': `-${appearance.body.settings.spacing.padding[3]}px`}">
					<Swiper
						:loop="true"
						:slides-per-view="4"
						:space-between="40"
						:centered-slides="true"
						class="category-carousel"
					>
						<SwiperSlide v-for="(category, index) in categories" :key="`category-${index}`" class="slide">
							<div class="position-relative">
								<img class="d-block w-100" src="https://www.bifolcomatty.co.uk/wp-content/uploads/2019/08/placeholder-square.jpg" alt />

								<div
									class="position-absolute bottom-0 start-0 d-flex align-items-center w-100 pt-5 pb-3 px-5"
									style="background: linear-gradient(0deg, rgba(0,0,0,.5) 0%, rgba(255,255,255,0) 100%); "
								>
									<p class="w-75 max-w-75 min-w-75" style="color: #fff; font-size: 24px">
										{{ category.names?.cz || category.names?.en || 'Unnamed' }}
									</p>
								</div>
							</div>
						</SwiperSlide>
					</Swiper>
				</div>

				<div class="d-flex flex-row flex-wrap gap-4 mt-5">
					<div v-for="(product, index) in productsArray" :key="`product-${index}`" style="width: calc(100% / 3 - 1rem)">
						<div class="position-relative">
							<div style="aspect-ratio: 1; background: url('https://www.bifolcomatty.co.uk/wp-content/uploads/2019/08/placeholder-square.jpg') center/cover">
								<img class="d-block w-100" :src="product.image" alt="" />
							</div>

							<div
								class="position-absolute bottom-0 start-0 d-flex align-items-center w-100 pt-5 px-3"
								style="background: linear-gradient(0deg, rgba(0,0,0,.5) 0%, rgba(255,255,255,0) 100%); "
							>
								<div class="d-flex align-items-end justify-content-between w-100">
									<p class="w-75 max-w-75 min-w-75" style="color: #fff; font-size: 20px">
										{{ product.label }}

										<br>

										{{ product.price }} Kč
									</p>

									<UiButtonAmount
										:settings="general.elements.buttonAmount2.settings"
										:product="product"
										class="mb-3"
									>
										<template #default="{ count }">
											<span style="color: #fff; font-size: 20px">{{ count }}</span>
										</template>
									</UiButtonAmount>
								</div>
							</div>
						</div>
					</div>
				</div>
			</template>

			<template v-else>
				<ProductsList :products="productsArray">
					<template #item="product">
						<div class="d-flex align-items-start">
							<div class="d-flex flex-row justify-content-between align-items-start flex-grow-1 pe-4">
								<p>
									{{ product.label }}
								</p>

								<p class="ps-3">
									{{ product.price }}&nbsp;Kč
								</p>
							</div>

							<UiButtonAmount :product="product" :settings="general.elements.buttonAmount1.settings">
								<template #default="{ count }">
									<UiText :settings="general.elements.heading4.settings">
										{{ count }}
									</UiText>
								</template>
							</UiButtonAmount>
						</div>

						<hr class="border-3" style="color: #000; opacity: 1;">
					</template>
				</ProductsList>
			</template>

			<UiElement class="position-sticky bottom-0 start-0 w-100 px-0" :settings="appearance.body.settings">
				<div class="d-flex justify-content-end gap-4">
					<router-link :to="`/${route.params.kioskId}/summary`" class="w-50 text-decoration-none">
						<UiButtonArrow :settings="button1" :body="appearance.body.settings" right class="w-100">
							{{ translations?.total || '{total}' }} {{ total }} Kč
						</UiButtonArrow>
					</router-link>
				</div>
			</UiElement>
		</UiElement>
	</div>
</template>

<script setup>
	import 'swiper/swiper-bundle.css';
	import { computed } from 'vue';
	import { storeToRefs } from 'pinia';
	import { useThemeStore } from '@/stores/useThemeStore';
	import { useCatalogStore } from '@/stores/useCatalogStore';
	import { Swiper, SwiperSlide } from 'swiper/vue';
	import { useRoute } from 'vue-router';
	import { useOrderStore } from '@/stores/useOrderStore';

	import UiElement from '@/components/UI/UiElement.vue';
	import UiButtonAmount from '@/components/UI/UiButtonAmount.vue';
	import UiButtonArrow from '@/components/UI/UiButtonArrow.vue';
	import ProductsList from '@/components/ProductsList.vue';
	import UiText from '@/components/UI/UiText.vue';
	import AppHeader from '@/components/AppHeader.vue';
	import { useInheritedSettings } from '@/composables/useInheritedSettings';
	import { useTranslationsStore } from '@/stores/useTranslationsStore';

	const route = useRoute();
	const themeStore = useThemeStore();
	const catalogStore = useCatalogStore();
	const orderStore = useOrderStore();
	const translationsStore = useTranslationsStore();


	const { translations } = storeToRefs(translationsStore);
	const { theme } = storeToRefs(themeStore);
	const { categories, products } = storeToRefs(catalogStore);
	const { total } = storeToRefs(orderStore);

	const general = computed(() => theme.value.general);

	const page = computed(() => theme.value.pages.products);
	const appearance = computed(() => page.value.appearance);
	const elements = computed(() => page.value.elements);

	const productsArray = computed(() => {
		return products.value.map((item) => ({
			label: item.name,
			image: item.image,
			price: item.price,
			id: item.id,
		}));
	});

	const button1 = useInheritedSettings(computed(() => elements.value?.button1));
	const logo = useInheritedSettings(computed(() => elements.value?.logo));
	const headerButton1 = useInheritedSettings(computed(() => elements.value?.headerButton1));
	const headerButton2 = useInheritedSettings(computed(() => elements.value?.headerButton2));
</script>

<style scoped>
	.category-carousel {
		width: 100%;
		padding: 40px 0;
	}
	.slide {
		transition: transform 0.3s;
	}

	.swiper-slide-active {
		transform: scale(1.2);
		z-index: 10;
	}
</style>